import { useQuery } from "@apollo/client";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { Alert, Box, CircularProgress, Link, Stack } from "@mui/material";
import { DateField } from "components/Forms/DateField";
import { FormAutocomplete } from "components/Forms/FormAutocomplete";
import { FormTextField } from "components/Forms/FormTextField";
import { MultipleFacilityPicker } from "components/MultipleFacilityPicker";
import { TagPicker } from "components/TagPicker";
import { isSystemManagedDocumentType } from "encamp-shared/src/documents";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { gql } from "generated-graphql";
import {
  DocumentType,
  FacilityDocumentInput,
  MultipleFacilityPickerFragment,
  TagPickerFragment,
  TagType,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useUpsertDocument } from "hooks/useUpsertDocument";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import { type ReactNode, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";
import { documentTypeToLabel } from "util/constants";

import { DocumentDialogContainer, isPreviewable } from "./shared";

const MANIFEST_DOCUMENT_FOR_EDIT = gql(`
  query ManifestDocumentForEdit($id: ID!) {
    document(id: $id) {
      id
      title
      description
      documentType
      authoredAt
      fileExtension
      storageLink
      currentSubmissionDocument
      documentTags {
        id
        name
        tenantId
        isDisabled
        type
      }
      facilities {
        id
        name
      }
      sourceWasteManifest {
        id
      }
      sourceProcessedManifest {
        id
      }
    }
  }
`);

type ManifestDocumentDialogProps = {
  documentId: string;
  tenantId: string;
  onClose: () => void;
  onComplete?: () => void;
};

type FormValues = {
  title: string;
  documentType: DocumentType;
  authoredAt: string | null;
  description: string;
  documentTags: TagPickerFragment[];
  facilities: FacilityDocumentInput[];
};

/**
 * Edit dialog for a manifest document opened from the Documents surface.
 *
 * Manifest documents have two separate concerns: document management (rename,
 * tags, associations) lives here, while the manifest *data* and its review
 * lifecycle live in the Waste module. This is a focused, self-contained dialog —
 * it deliberately does NOT reuse the general AI-metadata review form (which
 * doesn't apply to manifests) and instead links out to the manifest review page.
 * Saving goes through `upsertDocument` without a metadata payload, so it never
 * touches the document's review state (that belongs to the manifest flow).
 */
export function ManifestDocumentDialog({
  documentId,
  tenantId,
  onClose,
  onComplete,
}: ManifestDocumentDialogProps) {
  const { data, loading } = useQuery(MANIFEST_DOCUMENT_FOR_EDIT, {
    variables: { id: documentId },
  });
  const doc = data?.document;

  const { featureFlags } = useFeatureFlags();
  const { isStaff } = useCurrentUser();
  const canViewWaste = !!featureFlags[FeatureFlagKey.WasteModule] || isStaff;

  // Signed URL for the side-by-side document preview (PDF/image/etc.).
  const { data: downloadData, loading: downloadLoading } = useQuery(
    GET_DOCUMENT_DOWNLOAD_LINK_QUERY,
    { variables: { id: documentId } }
  );
  const canPreview = isPreviewable(doc?.fileExtension);
  const previewUrl = canPreview ? (downloadData?.getDocumentDownloadLink ?? undefined) : undefined;
  // Keep the preview area in a skeleton state until BOTH the document (for its
  // file extension) and its signed URL have resolved, so the dialog renders at a
  // stable size and FilePreview mounts exactly once instead of flashing.
  const previewLoading = loading || downloadLoading;

  const { upsertDocument, loading: saving } = useUpsertDocument();

  // Map the loaded document onto form values. Using RHF's `values` prop instead
  // of a reset-in-useEffect keeps the form in sync with server data without
  // wiping in-progress edits when the document is refetched/updated in the cache
  // — `keepDirtyValues` preserves any field the user has already touched.
  const formValues = useMemo<FormValues>(
    () => ({
      title: doc?.title ?? "",
      documentType: doc?.documentType ?? DocumentType.RcraManifest,
      authoredAt: doc?.authoredAt ?? null,
      description: doc?.description ?? "",
      documentTags: doc?.documentTags ?? [],
      facilities: (doc?.facilities ?? []).map((f) => ({ id: f.id, name: f.name ?? undefined })),
    }),
    [doc]
  );

  const { control, handleSubmit } = useForm<FormValues>({
    values: formValues,
    resetOptions: { keepDirtyValues: true },
  });

  // Lock the type picker when reassigning would be rejected by the upsert
  // resolver: (1) system-managed types like RcraManifestPrintedCopy are owned by
  // ingest, and (2) a document with parsed manifest data can't be reclassified
  // (it would orphan the linked WasteManifest). A manifest-typed doc with no data
  // yet stays editable so genuine misclassifications remain fixable.
  const hasLinkedManifest = !!doc?.sourceWasteManifest?.id;
  const typeLocked = isSystemManagedDocumentType(doc?.documentType) || hasLinkedManifest;
  const typeLockedHelperText = hasLinkedManifest
    ? "Type can't be changed while parsed manifest data is attached."
    : undefined;
  const documentTypeOptions = useMemo(() => {
    const current = doc?.documentType;
    const selectable = Object.values(DocumentType).filter((t) => !isSystemManagedDocumentType(t));
    const all = current && !selectable.includes(current) ? [current, ...selectable] : selectable;
    return all.map((type) => ({ label: documentTypeToLabel(type), value: type }));
  }, [doc?.documentType]);

  // Only make a claim the dialog can actually be sure of: if this document is
  // linked to a reconciled manifest (and the user can reach Waste), offer the
  // link. Otherwise stay neutral — we can't reliably tell "not yet processed"
  // from "orphaned duplicate" from "never parsed" here, and guessing produces
  // misleading copy. Those lifecycle states are surfaced accurately in the staff
  // Manifest Documents grid instead.
  const processedManifestId = doc?.sourceProcessedManifest?.id;

  const manifestBanner: ReactNode =
    canViewWaste && processedManifestId ? (
      <>
        This is a manifest document. Edit its details here, or{" "}
        <Link
          component={RouterLink}
          to={`/o/${tenantId}/waste/manifests/${processedManifestId}`}
          onClick={onClose}
        >
          review the manifest data in Waste
          <OpenInNew fontSize="inherit" sx={{ verticalAlign: "text-bottom", ml: 0.25 }} />
        </Link>
        .
      </>
    ) : (
      "This is a manifest document."
    );

  const onSave = handleSubmit(async (values) => {
    const newFacilityIds = values.facilities.map((f) => f.id);
    const originalFacilityIds = (doc?.facilities ?? []).map((f) => f.id);
    const associations = values.facilities.map((f) => ({ tenantId, facilityId: f.id ?? "" }));
    const removeAssociations = originalFacilityIds
      .filter((id) => !newFacilityIds.includes(id))
      .map((id) => ({ tenantId, facilityId: id }));

    await upsertDocument({
      documentData: {
        id: documentId,
        tenantId,
        documentType: values.documentType,
        title: values.title,
        authoredAt: values.authoredAt,
        description: values.description,
        fileExtension: doc?.fileExtension,
        storageLink: doc?.storageLink,
        documentTags: values.documentTags.map((t) => ({ id: t.id, name: t.name })),
      },
      associations,
      removeAssociations,
      currentSubmissionDocument: doc?.currentSubmissionDocument ?? false,
    });

    onComplete?.();
    onClose();
  });

  return (
    <DocumentDialogContainer
      open
      onClose={onClose}
      title="Edit Manifest Document"
      fullWidth
      maxWidth="lg"
      // Fixed paper height so the side-by-side PDF preview has a height to fill;
      // without it the preview column collapses to zero height and renders blank.
      sx={{ "& .MuiDialog-paper": { height: "80vh" } }}
      previewUrl={previewUrl}
      previewLoading={previewLoading}
      fileExtension={doc?.fileExtension ?? undefined}
      showPreviewBoxIfFailed
      formColumnSize={{ xs: 5 }}
      previewColumnSize={{ xs: 7 }}
      primaryActionButtonProps={{
        onClick: onSave,
        loading: saving,
        saveText: "Save",
      }}
    >
      {/* The form column is a `<Grid container>` (flex with wrap); a plain child
          sizes to its content, so the column would shrink to the banner text
          when the short "This is a manifest document." copy is shown. Pin to
          full width so the layout is identical regardless of banner length. */}
      <Stack gap={2} sx={{ pt: 1, flex: 1, width: "100%", minWidth: 0 }}>
        <Alert severity="info" icon={false}>
          {manifestBanner}
        </Alert>

        {loading && !doc ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <FormTextField name="title" label="Title" control={control} />

            <FormAutocomplete
              name="documentType"
              label="Document Type"
              control={control}
              autocompleteItems={documentTypeOptions}
              disabled={typeLocked}
              helperText={typeLockedHelperText}
            />

            <DateField name="authoredAt" label="Date Authored" control={control} />

            <FormTextField
              name="description"
              label="Description"
              control={control}
              textFieldProps={{ multiline: true, minRows: 1, maxRows: 6 }}
            />

            <Controller
              name="documentTags"
              control={control}
              render={({ field, fieldState }) => (
                <TagPicker {...field} {...fieldState} tagType={TagType.Document} label="Tags" />
              )}
            />

            <Controller
              name="facilities"
              control={control}
              render={({ field, fieldState }) => (
                <MultipleFacilityPicker
                  {...field}
                  {...fieldState}
                  value={(field.value ?? []).map((f) => ({ id: f.id ?? "", name: f.name ?? "" }))}
                  defaultSearchTerm={`tenantId:${tenantId}`}
                  label="Facilities"
                  onChange={(facilities: MultipleFacilityPickerFragment[] | null) => {
                    field.onChange(
                      (facilities ?? []).map((f) => ({ id: f.id, name: f.name ?? undefined }))
                    );
                  }}
                />
              )}
            />
          </>
        )}
      </Stack>
    </DocumentDialogContainer>
  );
}
